import { useMemo } from "react";

import { type ReviewQuestionType } from "../../redesign/Workplace/Review/types";
import {
  type NumberQuestionConfig,
  type PostShiftReviewStep,
  type QuestionMappings,
  QuestionTag,
} from "../types";

interface StepConfig {
  type: PostShiftReviewStep;
  required: boolean;
  questionId?: string;
  options?: Array<{ id: string; displayText: string }>;
  questionText?: string;
  questionType?: ReviewQuestionType;
  numberConfig?: NumberQuestionConfig;
}

export interface UsePostShiftReviewStepsProps {
  questionMappings?: QuestionMappings;
  isError?: boolean;
}

const getQuestionTextForTag = (tag: QuestionTag): string => {
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (tag) {
    case QuestionTag.SOFTWARE: {
      return "What software was used for documentation?";
    }

    case QuestionTag.SUPPLIES: {
      return "Did you have sufficient supplies during the shift?";
    }

    case QuestionTag.PATIENTS_PER_NURSE: {
      return "How many residents were you responsible for during this shift?";
    }

    case QuestionTag.PARKING: {
      return "Tell us about parking at this location";
    }

    default: {
      return "";
    }
  }
};

export function usePostShiftReviewSteps(props: UsePostShiftReviewStepsProps) {
  const { questionMappings, isError } = props;

  return useMemo(() => {
    const baseSteps: StepConfig[] = [{ type: "pick-rating", required: true }];

    const questionSteps: StepConfig[] = [];

    if (!isError && questionMappings) {
      // Only include write-review step if we have a commentQuestionId
      if (questionMappings.commentQuestionId) {
        questionSteps.push({ type: "write-review", required: true });
      }

      // Process in tag order to ensure consistent step sequence
      const orderedTags = [
        QuestionTag.SOFTWARE,
        QuestionTag.SUPPLIES,
        QuestionTag.PATIENTS_PER_NURSE,
        QuestionTag.PARKING,
      ];

      for (const tag of orderedTags) {
        const questionId = questionMappings.tagToQuestionId[tag];
        if (questionId) {
          const options = questionMappings.questionOptions[questionId];
          const numberConfig = questionMappings.questionNumberConfig[questionId];
          const questionType = questionMappings.questionTypes[questionId];

          questionSteps.push({
            type: tag as PostShiftReviewStep,
            required: false,
            questionId,
            options,
            questionText: getQuestionTextForTag(tag),
            questionType,
            numberConfig,
          });
        }
      }
    }

    const allSteps = [...baseSteps, ...questionSteps, { type: "end" as const, required: true }];

    return {
      steps: allSteps,
      stepTypes: allSteps.map((step) => step.type),
      getStepConfig: (stepType: PostShiftReviewStep) =>
        allSteps.find((step) => step.type === stepType),
    };
  }, [questionMappings, isError]);
}
